import { h, mount } from "@/lib/dom";
import { loading, button, openModal } from "@/components/ui";
import { topicsService, pbErrorMessage } from "@/services";
import { icon } from "@/lib/icons";
import { navigate } from "@/app/router";
import {
  getStreak,
  getWeekSummary,
  getLocalHeatmapData,
  getTopicStats,
} from "@/services/learn-stats.service";
import { topicStats } from "@/services/fsrs";
import type { Topic } from "@/types/models";

export function StatsPage(): HTMLElement {
  const container = h("div", null, loading());

  void (async () => {
    try {
      const topics = await topicsService.listAll();
      mount(container, renderStats(topics));
    } catch (err) {
      mount(container, h("div", { class: "card card-pad field-error" }, pbErrorMessage(err)));
    }
  })();

  return container;
}

function renderStats(topics: Topic[]): HTMLElement {
  const streak = getStreak();
  const week = getWeekSummary();
  const heatmap = getLocalHeatmapData(84); // 12 Wochen
  const quizPct = week.quizTotal > 0 ? Math.round((week.quizCorrect / week.quizTotal) * 100) : null;

  return h("div", { class: "stats-page" },
    h("div", { class: "page-header" },
      h("div", null,
        h("h1", null, "Statistik"),
        h("p", { class: "page-subtitle" }, "Dein Lernfortschritt auf einen Blick.")
      )
    ),

    // ---- Kennzahlen ----
    h("div", { class: "stats-kpi-row" },
      kpiCard("🔥", streak > 0 ? `${streak}` : "0", "Tage am Stück", streak >= 7 ? "accent" : ""),
      kpiCard("📇", String(week.cards), "Karten diese Woche", ""),
      quizPct !== null
        ? kpiCard("🎯", `${quizPct}%`, "Quiz-Trefferquote (7 Tage)", quizPct >= 70 ? "green" : quizPct >= 40 ? "" : "red")
        : kpiCard("🎯", "–", "Quiz-Trefferquote", ""),
      kpiCard("📅", String(week.activeDays) + "/7", "Aktive Tage diese Woche", "")
    ),

    // ---- Heatmap (kompakte Karte, Detail im Popup) ----
    h("div", { class: "card card-pad heatmap-teaser" },
      h("div", { class: "row", style: "align-items:center" },
        h("div", null,
          h("h3", { class: "section-title", style: "margin-bottom:2px" }, "Aktivitäts-Kalender"),
          h("p", { class: "muted text-sm" }, `${activeInRange(heatmap)} aktive Tage in 12 Wochen`)
        ),
        h("span", { class: "spacer" }),
        button({ label: "Heatmap öffnen", icon: "calendar", variant: "ghost", size: "sm",
          onClick: () => openModal({
            title: "Aktivitäts-Heatmap (12 Wochen)",
            wide: true,
            body: h("div", { class: "heatmap-modal-body" }, renderHeatmap(heatmap)),
          }),
        })
      )
    ),

    // ---- Pro-Thema ----
    topics.length
      ? h("div", { class: "card card-pad" },
          h("h3", { class: "section-title", style: "margin-bottom:12px" }, "Nach Thema"),
          h("div", { class: "col", style: "gap:8px" },
            ...topics
              .filter((t) => t.state !== "archived")
              .map((t) => topicStatRow(t))
          )
        )
      : null
  );
}

function activeInRange(data: { date: string; count: number }[]): number {
  return data.filter((d) => d.count > 0).length;
}

function kpiCard(emoji: string, value: string, label: string, variant: string): HTMLElement {
  return h("div", { class: `stats-kpi ${variant}` },
    h("div", { class: "stats-kpi-icon" }, emoji),
    h("div", { class: "stats-kpi-value" }, value),
    h("div", { class: "stats-kpi-label" }, label)
  );
}

function renderHeatmap(data: { date: string; count: number }[]): HTMLElement {
  const map = new Map(data.map((d) => [d.date, d.count]));
  const maxCount = Math.max(1, ...data.map((d) => d.count));

  // 84 Tage = 12 Wochen, aufgeteilt in 12 Spalten × 7 Zeilen
  const days: { date: string; count: number }[] = [];
  const DAY = 86_400_000;
  const now = Date.now();
  for (let i = 83; i >= 0; i--) {
    const d = new Date(now - i * DAY).toISOString().slice(0, 10);
    days.push({ date: d, count: map.get(d) ?? 0 });
  }

  const cells = days.map(({ date, count }) => {
    const intensity = count === 0 ? 0 : Math.ceil((count / maxCount) * 4);
    const [, m, dd] = date.split("-");
    return h("div", {
      class: `heatmap-cell heat-${intensity}`,
      title: `${dd}.${m}: ${count} Aktivitäten`,
    });
  });

  return h("div", null,
    h("div", { class: "heatmap-grid" }, ...cells),
    h("div", { class: "heatmap-legend" },
      h("span", { class: "muted text-sm" }, "weniger"),
      ...[0, 1, 2, 3, 4].map((i) => h("div", { class: `heatmap-cell heat-${i}` })),
      h("span", { class: "muted text-sm" }, "mehr")
    )
  );
}

function topicStatRow(topic: Topic): HTMLElement {
  const ts = getTopicStats(topic.id);
  const fsrs = topicStats(topic.id);
  const quoteText = ts.quizQuote >= 0 ? `${ts.quizQuote}% Quiz` : "kein Quiz";
  const cardText = `${fsrs.due} fällig · ${fsrs.new_} neu · ${fsrs.total} gesamt`;
  return h("button", {
    class: "stats-topic-row",
    type: "button",
    onclick: () => navigate(`/topic/${topic.id}/learn`),
  },
    h("div", { class: "stats-topic-title" }, topic.title),
    h("div", { class: "stats-topic-meta muted text-sm" },
      h("span", null, cardText),
      h("span", { class: "stats-dot" }),
      h("span", null, quoteText),
      h("span", { class: "spacer" }),
      h("span", { class: "stats-arrow", html: icon("chevron-right", 14) })
    )
  );
}
